import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router'
import { Card } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { PlusCircle, Pencil, Trash2, Eye, Lock, Globe } from 'lucide-react'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog"
import { API } from '@/api'
import { useToast } from '@/components/ui/use-toast'
import type { Story } from '@/types'
import { cn } from '@/lib/utils'

const MyWorks = () => {
  const navigate = useNavigate()
  const { toast } = useToast()
  const [loading, setLoading] = useState(true)
  const [works, setWorks] = useState<Story[]>([])

  // 获取我的作品列表
  const fetchMyWorks = async () => {
    setLoading(true)
    try {
      const data = await API.stories.getMyWorks()
      setWorks(data)
    } catch (error) {
      console.error('Failed to fetch works:', error)
      toast({
        title: '错误',
        description: '获取作品列表失败',
        variant: 'destructive'
      })
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchMyWorks()
  }, [])

  // 处理删除作品
  const handleDelete = async (id: string) => {
    try {
      await API.stories.delete(id)
      toast({
        title: '成功',
        description: '作品已删除'
      })
      // 重新获取列表
      fetchMyWorks()
    } catch (error) {
      console.error('Failed to delete work:', error)
      toast({
        title: '错误',
        description: '删除作品失败',
        variant: 'destructive'
      })
    }
  }

  if (loading) {
    return (
      <div className="w-full min-h-[200px] flex items-center justify-center">
        加载中...
      </div>
    )
  }

  return (
    <div className="w-full">
      {/* 顶部标题和新建按钮 */}
      <div className="flex items-center justify-between mb-8">
        <h2 className="text-2xl font-bold text-gray-800">我的作品</h2>
        <Button
          onClick={() => navigate('/create')}
          className="flex items-center gap-2 bg-gradient-to-r from-primary via-accent to-secondary hover:opacity-90"
        >
          <PlusCircle className="w-4 h-4" />
          <span>新建绘本</span>
        </Button>
      </div>
      
      {/* 作品网格 */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
        {works.map((work) => (
          <Card 
            key={work.id} 
            className="group cursor-pointer overflow-hidden hover:shadow-xl transition-all duration-300 hover:scale-105"
          >
            {/* 封面图片 */}
            <div className="relative aspect-[4/3] overflow-hidden rounded-t-xl">
              <img
                src={work.coverImageUrl || '/images/placeholder.jpg'}
                alt={work.title}
                className={cn(
                  "object-cover w-full h-full transform transition-transform duration-300",
                  work.status !== 'creating' && "group-hover:scale-110",
                  work.status === 'creating' && "opacity-30"
                )}
              />
              {work.status === 'creating' && (
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="text-center">
                    <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-primary border-t-transparent mb-2" />
                    <p className="text-primary font-medium">正在创作中，请稍等...</p>
                  </div>
                </div>
              )}
              <div className={cn(
                "absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent",
                work.status === 'creating' ? 'opacity-0' : 'opacity-0 group-hover:opacity-100 transition-opacity'
              )} />
            </div>
            
            {/* 内容区域 */}
            <div className="p-4">
              <h3 className="text-lg font-bold text-gray-800 line-clamp-1 mb-2 font-comic">
                {work.title}
              </h3>
              <div className="flex items-center justify-between text-sm text-gray-500 mb-4">
                <span>创建于 {new Date(work.createdAt).toLocaleDateString()}</span>
                <div className="flex items-center gap-1.5">
                  {work.status === 'published' && (
                    <span className="text-gray-400">
                      {work.visibility === 'public' ? (
                        <Globe className="w-3.5 h-3.5" />
                      ) : (
                        <Lock className="w-3.5 h-3.5" />
                      )}
                    </span>
                  )}
                  <span className={`px-2 py-0.5 rounded-full text-xs ${
                    work.status === 'published'
                      ? 'bg-green-100 text-green-600'
                      : 'bg-blue-100 text-blue-600'
                  }`}>
                    {work.status === 'published' ? '已发布' : '创作中'}
                  </span>
                </div>
              </div>
              <div className="flex justify-between gap-2">
                <Button 
                  variant="outline" 
                  size="sm" 
                  className={cn(
                    "flex-1 flex items-center justify-center gap-1",
                    work.status !== 'published' && "opacity-50 cursor-not-allowed"
                  )}
                  onClick={() => {
                    if (work.status === 'published') {
                      navigate(`/story/${work.id}`, { 
                        state: { from: 'my-works' }
                      })
                    }
                  }}
                  disabled={work.status !== 'published'}
                >
                  <Eye className="w-4 h-4" />
                  <span>查看</span>
                </Button>
                
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button 
                      variant="outline" 
                      size="sm"
                      className={cn(
                        "flex-1 flex items-center justify-center gap-1",
                        (work.status === 'published' && work.visibility === 'public')
                          ? "opacity-50 cursor-not-allowed text-gray-400 hover:text-gray-400 hover:border-gray-200"
                          : "text-red-500 hover:text-red-600 hover:border-red-200"
                      )}
                      disabled={work.status === 'published' && work.visibility === 'public'}
                    >
                      <Trash2 className="w-4 h-4" />
                      <span>删除</span>
                    </Button>
                  </AlertDialogTrigger>
                  {!(work.status === 'published' && work.visibility === 'public') && (
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>确认删除</AlertDialogTitle>
                        <AlertDialogDescription>
                          你确定要删除"{work.title}"吗？此操作不可撤销。
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>取消</AlertDialogCancel>
                        <AlertDialogAction
                          onClick={() => handleDelete(work.id)}
                          className="bg-red-500 hover:bg-red-600 text-white"
                        >
                          确认删除
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  )}
                </AlertDialog>
              </div>
            </div>
          </Card>
        ))}
      </div>
    </div>
  )
}

export default MyWorks 